import { Dialogs, TokenReplacement } from 'dspx-core';
import { PageActionButtonPath } from '../services/html-helper.service';
import { HideableGridConfiguration } from '../services/form-data.service';

// This is used to open a dialog from the callback of another dialog being closed.
// Unfortunately there is no reliable way to wait for the dialog to close.
export const minWaitForDialogToCloseMs = 200;

export const AppSelector = 'app-root';

export const HeaderSelector = 'dspx-card-set-header';
export const HeaderMenuToggleSelector = '#topbar-left-menu-button';

export const UiDateFormat = 'dd/MM/yyyy';

export const noFilterOptionCode = 'b7cfb3a557074e439549aaa17efcb7ff'; // This is to ensure there is no conflict with other options.

export class APPLICATION {
  static readonly title = 'Integrity forms';
  static readonly logo = '/assets/images/sgv-logo.png';
};

export class APP_DATA_KEYS {
  static readonly maxGridRowCount = 'maxGridRowCount';
  static readonly applicationUrl = 'applicationUrl';
  static readonly googleRecaptchaSiteKey = 'googleRecaptchaSiteKey';
  static readonly previousModel = 'previousModel';
};

export const OVERVIEW_CARDSET_LAYOUT_FIELD_TYPE_REPLACEMENTS: TokenReplacement[] = [
  { old: 'tabs', new: 'card-set-tabs-flat' },
  { old: 'page', new: 'card-set-page' },
  { old: 'page-repeating', new: 'card-set-page-repeating' },
  { old: 'page-info', new: 'card-set-page-info' },
  { old: 'page-list', new: 'card-set-page-list' },
  { old: 'page-overview', new: 'card-set-page-overview' },
  { old: 'section', new: 'card-section-overview' },
  { old: 'section-repeating', new: 'card-section-repeating' },
  { old: 'section-overview', new: 'card-section-overview' }
];

export class FORM_ACTIONS {
  static readonly integrityOfficerListForm = {
    view: 'view',
    edit: 'edit'
  } as const;

  static readonly referenceDataListForm = {
    new: 'new',
    view: 'view',
    edit: 'edit',
    custom: 'custom',
  } as const;

  static readonly coiFormListForm = {
    new: 'new',
    view: 'view',
    edit: 'edit',
    colLink: 'colLink',
    reopenWithComment: 'reopenWithComment',
    reopen: 'reopen',
    delete: 'delete',
    custom: 'custom'
  } as const;

  static readonly dopiFormListForm = {
    new: 'new',
    view: 'view',
    edit: 'edit',
    colLink: 'colLink',
    delete: 'delete',
    duplicate: 'duplicate',
    custom: 'custom'
  } as const;

  static readonly gbhFormListForm = {
    new: 'new',
    view: 'view',
    edit: 'edit',
    colLink: 'colLink',
    delete: 'delete',
    custom: 'custom'
  } as const;

  static readonly coiForm = {
    downloadVersion: 'downloadVersion'
  } as const;

  static readonly approverCoiForm = {
    downloadVersion: 'downloadVersion'
  } as const;

  static readonly dopiForm = {
    downloadVersion: 'downloadVersion'
  } as const;

  static readonly approverDopiForm = {
    downloadVersion: 'downloadVersion'
  } as const;

  static readonly gbhForm = {
    downloadVersion: 'downloadVersion'
  } as const;

  static readonly approverGbhForm = {
    downloadVersion: 'downloadVersion'
  } as const;
};

export class FORM_CUSTOM_ACTIONS {
  static readonly referenceDataListForm = {
    bulkUpload: 'bulkUpload',
  } as const;
  static readonly coiFormListForm = {
    customAdd: 'customAdd',
    bulkExport: 'bulkExport',
    requestFromExternalUser: 'requestFromExternalUser'
  } as const;
  static readonly dopiFormListForm = {
    customAdd: 'customAdd',
    bulkExport: 'bulkExport',
    requestFromExternalUser: 'requestFromExternalUser'
  } as const;
  static readonly gbhFormListForm = {
    customAdd: 'customAdd',
    bulkExport: 'bulkExport'
  } as const;
};

export class POP_UP_CODES {
  static readonly common = {
    clientError: Dialogs.CLIENT_ERROR,
    serverError: Dialogs.SERVER_ERROR,
    validationError: Dialogs.VALIDATION,
    authorisationError: Dialogs.UNAUTHORISED,
    sessionTimeout: Dialogs.SESSION_TIMEOUT,
    saveSuccessful: 'saveSuccessful',
    confirmDocumentDeletion: 'confirmDocumentDeletion',
    submitSuccessful: 'submitSuccessful'
  } as const;
  static readonly referenceData = {
    duplicateCategoryCode: 'duplicateCategoryCode',
    duplicateCategoryValue: 'duplicateCategoryValue',
    duplicateCode: 'duplicateCode',
    duplicateValue: 'duplicateValue',
    duplicateDisplayOrder: 'duplicateDisplayOrder',
    deletedValues: 'deletedValues'
  } as const;
  static readonly coiFormList = {
    reopenWithComment: 'reopenWithComment',
    reopenConfirmation: 'reopenConfirmation',
    reopenSuccessful: 'reopenSuccessful',
    deleteConfirmation: 'deleteConfirmation',
    deleteSuccessful: 'deleteSuccessful',
    requestFromExternalUser: 'requestFromExternalUser',
    requestFromExternalUserSuccessful: 'requestFromExternalUserSuccessful',
    declarerIsInternal: 'declarerIsInternal',
    interestedPartySameAsApprover: 'interestedPartySameAsApprover'
  } as const;
  static readonly coiForm = {
    reopenWithComment: 'reopenWithComment',
    reopenConfirmation: 'reopenConfirmation',
    reopenSuccessful: 'reopenSuccessful',
    deleteConfirmation: 'deleteConfirmation',
    deleteSuccessful: 'deleteSuccessful',
    consentNotSet: 'consentNotSet',
    interestedPartySameAsApprover: 'interestedPartySameAsApprover',
    externalSaveConfirmation: 'externalSaveConfirmation'
  } as const;
  static readonly approverCoiForm = {
    review: 'review',
    reviewSuccessful: 'reviewSuccessful',
    nextReviewDateMustBeSpecified: 'nextReviewDateMustBeSpecified',
    nextReviewDateMustBeEmpty: 'nextReviewDateMustBeEmpty',
    nextReviewDateMustBeInTheFuture: 'nextReviewDateMustBeInTheFuture',
    reopenWithComment: 'reopenWithComment',
    reopenConfirmation: 'reopenConfirmation',
    reopenSuccessful: 'reopenSuccessful',
    approveConfirmation: 'approveConfirmation',
    returnForActionConfirmation: 'returnForActionConfirmation',
    approveSuccessful: 'approveSuccessful',
    returnedForAction: 'returnedForAction'
  } as const;
  static readonly dopiFormList = {
    deleteConfirmation: 'deleteConfirmation',
    deleteSuccessful: 'deleteSuccessful',
    duplicateConfirmation: 'duplicateConfirmation',
    duplicateSuccessful: 'duplicateSuccessful',
    requestFromExternalUser: 'requestFromExternalUser',
    requestFromExternalUserSuccessful: 'requestFromExternalUserSuccessful',
    declarerIsInternal: 'declarerIsInternal'
  } as const;
  static readonly dopiForm = {
    deleteConfirmation: 'deleteConfirmation',
    deleteSuccessful: 'deleteSuccessful',
    consentNotSet: 'consentNotSet',
    externalSaveConfirmation: 'externalSaveConfirmation'
  } as const;
  static readonly approverDopiForm = {
    approveConfirmation: 'approveConfirmation',
    returnForActionConfirmation: 'returnForActionConfirmation',
    approveSuccessful: 'approveSuccessful',
    returnedForAction: 'returnedForAction'
  } as const;
  static readonly gbhFormList = {
    deleteConfirmation: 'deleteConfirmation',
    deleteSuccessful: 'deleteSuccessful'
  } as const;
  static readonly gbhForm = {
    deleteConfirmation: 'deleteConfirmation',
    deleteSuccessful: 'deleteSuccessful',
    consentNotSet: 'consentNotSet'
  } as const;
  static readonly approverGbhForm = {
    approveConfirmation: 'approveConfirmation',
    returnForActionConfirmation: 'returnForActionConfirmation',
    approveSuccessful: 'approveSuccessful',
    returnedForAction: 'returnedForAction',
    consentNotSet: 'consentNotSet'
  } as const;
};

export class POP_UP_TEXTS {
  static readonly common = {
    confirmDocumentDeletion: '<p>Confirm the deletion of this document?</p>'
  } as const;
};

export class SERVER_ERROR_POP_UP_CODES {
  static readonly saveIntegrityOfficer = [
    // No error popup codes have been defined
  ];
  static readonly saveReferenceData = [
    POP_UP_CODES.referenceData.duplicateCategoryCode,
    POP_UP_CODES.referenceData.duplicateCategoryValue,
    POP_UP_CODES.referenceData.duplicateCode,
    POP_UP_CODES.referenceData.duplicateValue,
    POP_UP_CODES.referenceData.duplicateDisplayOrder,
    POP_UP_CODES.referenceData.deletedValues,
  ];
  static readonly saveCoiForm = [
    POP_UP_CODES.coiForm.consentNotSet,
    POP_UP_CODES.coiForm.interestedPartySameAsApprover
  ];
  static readonly reopenWithCommentCoiForm = [
    // No error popup codes have been defined
  ];
  static readonly reopenCoiForm = [
    // No error popup codes have been defined
  ];
  static readonly deleteCoiForm = [
    // No error popup codes have been defined
  ];
  static readonly saveApproverCoiForm = [
    // No error popup codes have been defined
  ];
  static readonly deleteApproverCoiForm = [
    // No error popup codes have been defined
  ];
  static readonly reviewCoiForm = [
    POP_UP_CODES.approverCoiForm.nextReviewDateMustBeSpecified,
    POP_UP_CODES.approverCoiForm.nextReviewDateMustBeEmpty,
    POP_UP_CODES.approverCoiForm.nextReviewDateMustBeInTheFuture
  ];
  static readonly requestCoiFormFromExternalUser = [
    POP_UP_CODES.coiFormList.declarerIsInternal,
    POP_UP_CODES.coiFormList.interestedPartySameAsApprover
  ];
  static readonly requestDopiFormFromExternalUser = [
    POP_UP_CODES.dopiFormList.declarerIsInternal
  ];
  static readonly saveDopiForm = [
    POP_UP_CODES.dopiForm.consentNotSet,
  ];
  static readonly deleteDopiForm = [
    // No error popup codes have been defined
  ];
  static readonly duplicateDopiForm = [
    // No error popup codes have been defined
  ];
  static readonly saveApproverDopiForm = [
    // No error popup codes have been defined
  ];
  static readonly deleteGbhForm = [
    // No error popup codes have been defined
  ];
  static readonly saveGbhForm = [
    POP_UP_CODES.gbhForm.consentNotSet
  ];
  static readonly saveApproverGbhForm = [
    POP_UP_CODES.approverGbhForm.consentNotSet
  ];
};

export class NAV_TAB_NUMBERS {
  static readonly dashboardCommon = {
    grid: 1,
  } as const;
  static readonly integrityOfficerForm = {
    integrityOfficer: 1
  } as const;
  static readonly referenceDataForm = {
    refDataCategory: 2,
    refDataValue: 3,
  } as const;
  static readonly coiFormListForm = {
    coiForms: 1
  } as const;
  static readonly coiForm = {
    yourDetails: 1,
    yourApprover: 2,
    yourDeclaration: 3,
    managementPlan: 4,
    attestation: 5
  } as const;
  static readonly approverCoiForm = {
    navTabPlaceholder: 1
  } as const;
  static readonly dopiFormListForm = {
    dopiForms: 1
  } as const;
  static readonly dopiForm = {
    yourDetails: 1,
    yourApprover: 2,
    privateInterests: 3,
    probity: 4,
    attestation: 5
  } as const;
  static readonly approverDopiForm = {
    navTabPlaceholder: 1
  } as const;
  static readonly gbhFormListForm = {
    gbhForms: 1
  } as const;
  static readonly gbhForm = {
    yourDetails: 1,
    theOffer: 2,
    responseToTheOffer: 3
  } as const;
  static readonly approverGbhForm = {
    navTabPlaceholder: 1
  } as const;
};

export class PAGE_NUMBERS {
  static readonly dashboardCommon = {
    grid: 1,
  } as const;
  static readonly integrityOfficerForm = {
    integrityOfficer: 1
  } as const;
  static readonly referenceDataForm = {
    refDataCategory: 1,
    refDataValue: 1,
  } as const;
  static readonly coiFormListForm = {
    coiForms: 1
  } as const;
  static readonly coiForm = {
    yourDetails: 1,
    yourApprover: 1,
    yourDeclaration: 1,
    managementPlan: 1,
    attestation: 1
  } as const;
  static readonly approverCoiForm = {
    pagePlaceholder: 1
  } as const;
  static readonly dopiFormListForm = {
    dopiForms: 1
  } as const;
  static readonly dopiForm = {
    yourDetails: 1,
    yourApprover: 1,
    privateInterestsOtherEmployment: 1,
    privateInterestsOffices: 2,
    privateInterestsShareholdingsBusinessInterests: 3,
    privateInterestsTrusts: 4,
    privateInterestsRealEstate: 5,
    privateInterestsAgreements: 6,
    privateInterestsFamilyInterests: 7,
    privateInterestsOtherInterests: 8,
    probityBankruptcy: 1,
    probityInsolvency: 2,
    probityDisqualification: 3,
    probityPenalties: 4,
    probityCriminalAndCivilProceedings: 5,
    probityFindingsOfGuilt: 6,
    probityInquiriesAndInvestigations: 7,
    attestation: 1
  } as const;
  static readonly approverDopiForm = {
    pagePlaceholder: 1
  } as const;
  static readonly gbhFormListForm = {
    gbhForms: 1
  } as const;
  static readonly gbhForm = {
    yourDetails: 1,
    theOffer: 1,
    responseToTheOffer: 1
  } as const;
  static readonly approverGbhForm = {
    pagePlaceholder: 1
  } as const;
};

export class SECTION_CODES {
  static readonly referenceDataForm = {
    viewRefDataCategory: 'viewRefDataCategory',
    refDataCategory: 'refDataCategory',
    refDataValue: 'refDataValue',
  } as const;
  static readonly coiFormListForm = {
    resultSection: 'resultSection'
  } as const;
  static readonly coiForm = {
    yourApproverSection: 'yourApproverSection',
    yourDeclarationSection: 'yourDeclarationSection',
    attestationSection: 'attestationSection',
    versionHistorySection: 'versionHistorySection'
  } as const;
  static readonly approverCoiForm = {
    coiFormApproverReviewSection: 'coiFormApproverReviewSection',
    versionHistorySection: 'versionHistorySection'
  } as const;
  static readonly dopiFormListForm = {
    resultSection: 'resultSection'
  } as const;
  static readonly dopiForm = {
    yourDeclarationSection: 'yourDeclarationSection',
    yourApproverSection: 'yourApproverSection',
    attestationSection: 'attestationSection',
    versionHistorySection: 'versionHistorySection'
  } as const;
  static readonly approverDopiForm = {
    dopiFormApproverReviewSection: 'dopiFormApproverReviewSection',
    versionHistorySection: 'versionHistorySection'
  } as const;
  static readonly gbhFormListForm = {
    resultSection: 'resultSection'
  } as const;
  static readonly gbhForm = {
    yourDetailsSection: 'yourDetailsSection',
    responseDetailsSection: 'responseDetailsSection',
    offerDetailsSection: 'offerDetailsSection',
    versionHistorySection: 'versionHistorySection'
  } as const;
  static readonly approverGbhForm = {
    gbhFormApproverReviewSection: 'gbhFormApproverReviewSection',
    versionHistorySection: 'versionHistorySection'
  } as const;
};

export class QUESTION_CODES {
  static readonly integrityOfficerListForm = {
    integrityOfficersPlaceholder: 'integrityOfficersPlaceholder',
  } as const;
  static readonly referenceDataListForm = {
    referenceDataPlaceholder: 'referenceDataPlaceholder',
  } as const;
  static readonly referenceDataForm = {
    referenceCategoryCode: 'categoryCode',
    referenceCategoryText: 'categoryText',
    referenceCategoryIsActive: 'categoryStatus',
  } as const;
  static readonly coiFormListForm = {
    coiFormsFilter: 'coiFormsFilter',
    coiFormsPlaceholder: 'coiFormsPlaceholder'
  } as const;
  static readonly coiForm = {
    yourApprover: 'yourApprover',
    approverRole: 'approverRole',
    yourAssessment: 'yourAssessment',
    consent: 'consent',
    showVersionHistory: 'showVersionHistory',
    hideVersionHistory: 'hideVersionHistory',
    versionHistory: 'versionHistory'
  } as const;
  static readonly approverCoiForm = {
    consent: 'consent',
    approverComments: 'approverComments',
    satisfiedWithSuggestedPlan: 'satisfiedWithSuggestedPlan',
    needToReviewThePlan: 'needToReviewThePlan',
    planReviewDate: 'planReviewDate',
    appropriateApprover: 'appropriateApprover',
    agreeWithDeclarer: 'agreeWithDeclarer',
    showVersionHistory: 'showVersionHistory',
    hideVersionHistory: 'hideVersionHistory',
    versionHistory: 'versionHistory'

  } as const;
  static readonly dopiFormListForm = {
    dopiFormsFilter: 'dopiFormsFilter',
    dopiFormsPlaceholder: 'dopiFormsPlaceholder'
  } as const;
  static readonly dopiForm = {
    yourApprover: 'yourApprover',
    approverRole: 'approverRole',
    yourAssessment: 'yourAssessment',
    consent: 'consent',
    showVersionHistory: 'showVersionHistory',
    hideVersionHistory: 'hideVersionHistory',
    versionHistory: 'versionHistory'
  } as const;
  static readonly approverDopiForm = {
    consent: 'consent',
    approverComments: 'approverComments',
    approverAssessmentCoiFlag: 'approverAssessmentCoiFlag',
    hasDeclarerConflictOfInterest: 'hasDeclarerConflictOfInterest',
    showVersionHistory: 'showVersionHistory',
    hideVersionHistory: 'hideVersionHistory',
    versionHistory: 'versionHistory'
  } as const;
  static readonly gbhFormListForm = {
    gbhFormsFilter: 'gbhFormsFilter',
    gbhFormsPlaceholder: 'gbhFormsPlaceholder'
  } as const;
  static readonly gbhForm = {
    yourApprover: 'yourApprover',
    approverRole: 'approverPosition',
    decision: 'decision',
    offerorRelationship: 'offerorRelationship',
    officialItem: 'officialItem',
    consent: 'consent',
    showVersionHistory: 'showVersionHistory',
    hideVersionHistory: 'hideVersionHistory',
    versionHistory: 'versionHistory'
  } as const;
  static readonly approverGbhForm = {
    consent: 'consent',
    approveAcceptance: 'approveAcceptance',
    custodianship: 'custodianship',
    appropriateApprover: 'appropriateApprover',
    showVersionHistory: 'showVersionHistory',
    hideVersionHistory: 'hideVersionHistory',
    versionHistory: 'versionHistory'
  } as const;
};

export class FILTER_ACTIONS {
  static readonly common = {
    applyFilters: 'applyFilters',
    clearFilters: 'clearFilters'
  } as const;
};

export class FILTER_QUESTION_CODES {
  static readonly coiFormListForm = {
    coiId: 'coiId',
    submissionDateFrom: 'submissionDateFrom',
    submissionDateTo: 'submissionDateTo',
    typeId: 'typeId',
    declarerId: 'declarerId',
    approverId: 'approverId',
    hasCoi: 'hasCoi',
    statusId: 'statusId'
  } as const;
  static readonly dopiFormListForm = {
    dopiId: 'dopiId',
    submissionDateFrom: 'submissionDateFrom',
    submissionDateTo: 'submissionDateTo',    
    declarerId: 'declarerId',
    approverId: 'approverId',    
    statusId: 'statusId',
    isCoiFlagged: 'isCoiFlagged',
    isLatestDopiForDeclarer: 'isLatestDopiForDeclarer'
  } as const;
  static readonly gbhFormListForm = {
    gbhId: 'gbhId',
    submissionDateFrom: 'submissionDateFrom',
    submissionDateTo: 'submissionDateTo',
    declarerId: 'declarerId',
    approverId: 'approverId',
    offerorOrganisation: 'offerorOrganisation',
    statusId: 'statusId'
  } as const;
};

export class PAGE_ACTION_BUTTON_CODES {
  static readonly integrityOfficerForm = {
    cancel: 'btnCustomCancel'
  } as const;
  static readonly coiForm = {
    cancel: 'btnCustomCancel',
    downloadCopy: 'btnDownloadCopy',
    reopenWithComment: 'btnReopenWithComment',
    reopen: 'btnReopen',
    switchToEdit: 'btnSwitchToEdit',
    delete: 'btnDelete'
  } as const;
  static readonly approverCoiForm = {
    cancel: 'btnCustomCancel',
    switchToEdit: 'btnSwitchToEdit',
    returnForActionWithoutAssessment: 'btnReturnedWithoutAssessment',
    downloadCopy: 'btnDownloadCopy',
    reopenWithComment: 'btnReopenWithComment',
    reopen: 'btnReopen',
    review: 'btnReview'
  } as const;
  static readonly dopiForm = {
    cancel: 'btnCustomCancel',
    downloadCopy: 'btnDownloadCopy',
    switchToEdit: 'btnSwitchToEdit',
    delete: 'btnDelete'
  } as const;
  static readonly approverDopiForm = {
    cancel: 'btnCustomCancel',
    switchToEdit: 'btnSwitchToEdit',
    returnForActionWithoutAssessment: 'btnReturnedWithoutAssessment',
    downloadCopy: 'btnDownloadCopy'
  } as const;
  static readonly gbhForm = {
    cancel: 'btnCustomCancel',
    downloadCopy: 'btnDownloadCopy',
    switchToEdit: 'btnSwitchToEdit',
    delete: 'btnDelete'
  } as const;
  static readonly approverGbhForm = {
    cancel: 'btnCustomCancel',
    switchToEdit: 'btnSwitchToEdit',
    returnForActionWithoutAssessment: 'btnReturnedWithoutAssessment',
    downloadCopy: 'btnDownloadCopy'
  } as const;
};

export class OPTION_SET_CODES {
  static readonly gbhForm = {
    relationship: {
      supplier: 'supplier',
      fundedAgency: 'fundedagency',
      grantOrFundingApplicant: 'grantorfundingapplicant',
      communityOrganisation: 'communityorganisation',
      eventOrganiser: 'eventorganiser',
      publicSectorBody: 'publicsectorbody',
      lobbyist: 'lobbyist',
      clientOrServiceUser: 'clientorserviceuser',
      other: 'other',
      healthService: 'healthservice',
      university: 'university'
    },
    decision: {
      declined: 'declined',
      accepted: 'accepted'
    },
    yesNo: {
      yes: 'yes',
      no: 'no'
    }
  } as const;
  static readonly approverCoiForm = {
    yesNo: {
      yes: 'yes',
      no: 'no'
    },
    yesNoMoreInformationNeeded: {
      yes: 'yes',
      no: 'no',
      moreInformationNeeded: 'moreinformationneeded'
    }
  } as const;
  static readonly approverDopiForm = {
    yesNoMoreInformationNeeded: {
      yes: 'yes',
      no: 'no',
      moreInformationNeeded: 'moreinformationneeded'
    }
  } as const;
  static readonly approverGbhForm = {
    yesNo: {
      yes: 'yes',
      no: 'no'
    },
    yesNoMoreInformationNeeded: {
      yes: 'yes',
      no: 'no',
      moreInformationNeeded: 'moreinformationneeded'
    }
  } as const;
};

export class STATIC_PAGE_IDS {
  static readonly endOfSession = 'EndOfSession';
  static readonly collectionNotice = 'CollectionNotice';
};

export const READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS: { [form: string]: PageActionButtonPath[] } = {
  integrityOfficer: [
    [NAV_TAB_NUMBERS.integrityOfficerForm.integrityOfficer, PAGE_NUMBERS.integrityOfficerForm.integrityOfficer, PAGE_ACTION_BUTTON_CODES.integrityOfficerForm.cancel]
  ],
  referenceData: [
  ],
  coiForm: [
    [NAV_TAB_NUMBERS.coiForm.yourDetails, PAGE_NUMBERS.coiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.coiForm.cancel],
    [NAV_TAB_NUMBERS.coiForm.yourDetails, PAGE_NUMBERS.coiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.coiForm.downloadCopy],
    [NAV_TAB_NUMBERS.coiForm.yourDetails, PAGE_NUMBERS.coiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.coiForm.reopenWithComment],
    [NAV_TAB_NUMBERS.coiForm.yourDetails, PAGE_NUMBERS.coiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.coiForm.reopen],
    [NAV_TAB_NUMBERS.coiForm.yourDetails, PAGE_NUMBERS.coiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.coiForm.switchToEdit],
    [NAV_TAB_NUMBERS.coiForm.yourDetails, PAGE_NUMBERS.coiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.coiForm.delete],
    [NAV_TAB_NUMBERS.coiForm.yourApprover, PAGE_NUMBERS.coiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.coiForm.cancel],
    [NAV_TAB_NUMBERS.coiForm.yourApprover, PAGE_NUMBERS.coiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.coiForm.downloadCopy],
    [NAV_TAB_NUMBERS.coiForm.yourApprover, PAGE_NUMBERS.coiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.coiForm.reopenWithComment],
    [NAV_TAB_NUMBERS.coiForm.yourApprover, PAGE_NUMBERS.coiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.coiForm.reopen],
    [NAV_TAB_NUMBERS.coiForm.yourApprover, PAGE_NUMBERS.coiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.coiForm.switchToEdit],
    [NAV_TAB_NUMBERS.coiForm.yourApprover, PAGE_NUMBERS.coiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.coiForm.delete],
    [NAV_TAB_NUMBERS.coiForm.yourDeclaration, PAGE_NUMBERS.coiForm.yourDeclaration, PAGE_ACTION_BUTTON_CODES.coiForm.cancel],
    [NAV_TAB_NUMBERS.coiForm.yourDeclaration, PAGE_NUMBERS.coiForm.yourDeclaration, PAGE_ACTION_BUTTON_CODES.coiForm.downloadCopy],
    [NAV_TAB_NUMBERS.coiForm.yourDeclaration, PAGE_NUMBERS.coiForm.yourDeclaration, PAGE_ACTION_BUTTON_CODES.coiForm.reopenWithComment],
    [NAV_TAB_NUMBERS.coiForm.yourDeclaration, PAGE_NUMBERS.coiForm.yourDeclaration, PAGE_ACTION_BUTTON_CODES.coiForm.reopen],
    [NAV_TAB_NUMBERS.coiForm.yourDeclaration, PAGE_NUMBERS.coiForm.yourDeclaration, PAGE_ACTION_BUTTON_CODES.coiForm.switchToEdit],
    [NAV_TAB_NUMBERS.coiForm.yourDeclaration, PAGE_NUMBERS.coiForm.yourDeclaration, PAGE_ACTION_BUTTON_CODES.coiForm.delete],
    [NAV_TAB_NUMBERS.coiForm.managementPlan, PAGE_NUMBERS.coiForm.managementPlan, PAGE_ACTION_BUTTON_CODES.coiForm.cancel],
    [NAV_TAB_NUMBERS.coiForm.managementPlan, PAGE_NUMBERS.coiForm.managementPlan, PAGE_ACTION_BUTTON_CODES.coiForm.downloadCopy],
    [NAV_TAB_NUMBERS.coiForm.managementPlan, PAGE_NUMBERS.coiForm.managementPlan, PAGE_ACTION_BUTTON_CODES.coiForm.reopenWithComment],
    [NAV_TAB_NUMBERS.coiForm.managementPlan, PAGE_NUMBERS.coiForm.managementPlan, PAGE_ACTION_BUTTON_CODES.coiForm.reopen],
    [NAV_TAB_NUMBERS.coiForm.managementPlan, PAGE_NUMBERS.coiForm.managementPlan, PAGE_ACTION_BUTTON_CODES.coiForm.switchToEdit],
    [NAV_TAB_NUMBERS.coiForm.managementPlan, PAGE_NUMBERS.coiForm.managementPlan, PAGE_ACTION_BUTTON_CODES.coiForm.delete],
    [NAV_TAB_NUMBERS.coiForm.attestation, PAGE_NUMBERS.coiForm.attestation, PAGE_ACTION_BUTTON_CODES.coiForm.cancel],
    [NAV_TAB_NUMBERS.coiForm.attestation, PAGE_NUMBERS.coiForm.attestation, PAGE_ACTION_BUTTON_CODES.coiForm.downloadCopy],
    [NAV_TAB_NUMBERS.coiForm.attestation, PAGE_NUMBERS.coiForm.attestation, PAGE_ACTION_BUTTON_CODES.coiForm.reopenWithComment],
    [NAV_TAB_NUMBERS.coiForm.attestation, PAGE_NUMBERS.coiForm.attestation, PAGE_ACTION_BUTTON_CODES.coiForm.reopen],
    [NAV_TAB_NUMBERS.coiForm.attestation, PAGE_NUMBERS.coiForm.attestation, PAGE_ACTION_BUTTON_CODES.coiForm.switchToEdit],
    [NAV_TAB_NUMBERS.coiForm.attestation, PAGE_NUMBERS.coiForm.attestation, PAGE_ACTION_BUTTON_CODES.coiForm.delete]
  ],
  approverCoiForm: [
    [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverCoiForm.cancel],
    [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverCoiForm.switchToEdit],
    [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverCoiForm.returnForActionWithoutAssessment],
    [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverCoiForm.downloadCopy],
    [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverCoiForm.reopenWithComment],
    [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverCoiForm.reopen],
    [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverCoiForm.review]
  ],
  dopiForm: [
    [NAV_TAB_NUMBERS.dopiForm.yourDetails, PAGE_NUMBERS.dopiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.yourDetails, PAGE_NUMBERS.dopiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.yourDetails, PAGE_NUMBERS.dopiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.yourDetails, PAGE_NUMBERS.dopiForm.yourDetails, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.yourApprover, PAGE_NUMBERS.dopiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.yourApprover, PAGE_NUMBERS.dopiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.yourApprover, PAGE_NUMBERS.dopiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.yourApprover, PAGE_NUMBERS.dopiForm.yourApprover, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOtherEmployment, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOtherEmployment, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOtherEmployment, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOtherEmployment, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOffices, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOffices, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOffices, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOffices, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsShareholdingsBusinessInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsShareholdingsBusinessInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsShareholdingsBusinessInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsShareholdingsBusinessInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsTrusts, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsTrusts, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsTrusts, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsTrusts, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsRealEstate, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsRealEstate, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsRealEstate, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsRealEstate, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsAgreements, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsAgreements, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsAgreements, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsAgreements, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsFamilyInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsFamilyInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsFamilyInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsFamilyInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOtherInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOtherInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOtherInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.privateInterests, PAGE_NUMBERS.dopiForm.privateInterestsOtherInterests, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityBankruptcy, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityBankruptcy, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityBankruptcy, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityBankruptcy, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityInsolvency, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityInsolvency, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityInsolvency, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityInsolvency, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityDisqualification, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityDisqualification, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityDisqualification, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityDisqualification, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityPenalties, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityPenalties, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityPenalties, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityPenalties, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityCriminalAndCivilProceedings, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityCriminalAndCivilProceedings, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityCriminalAndCivilProceedings, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityCriminalAndCivilProceedings, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityFindingsOfGuilt, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityFindingsOfGuilt, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityFindingsOfGuilt, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityFindingsOfGuilt, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityInquiriesAndInvestigations, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityInquiriesAndInvestigations, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityInquiriesAndInvestigations, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.probity, PAGE_NUMBERS.dopiForm.probityInquiriesAndInvestigations, PAGE_ACTION_BUTTON_CODES.dopiForm.delete],
    [NAV_TAB_NUMBERS.dopiForm.attestation, PAGE_NUMBERS.dopiForm.attestation, PAGE_ACTION_BUTTON_CODES.dopiForm.cancel],
    [NAV_TAB_NUMBERS.dopiForm.attestation, PAGE_NUMBERS.dopiForm.attestation, PAGE_ACTION_BUTTON_CODES.dopiForm.downloadCopy],
    [NAV_TAB_NUMBERS.dopiForm.attestation, PAGE_NUMBERS.dopiForm.attestation, PAGE_ACTION_BUTTON_CODES.dopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.dopiForm.attestation, PAGE_NUMBERS.dopiForm.attestation, PAGE_ACTION_BUTTON_CODES.dopiForm.delete]
  ],
  approverDopiForm: [
    [NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder, PAGE_NUMBERS.approverDopiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverDopiForm.cancel],
    [NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder, PAGE_NUMBERS.approverDopiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverDopiForm.switchToEdit],
    [NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder, PAGE_NUMBERS.approverDopiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverDopiForm.returnForActionWithoutAssessment],
    [NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder, PAGE_NUMBERS.approverDopiForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverDopiForm.downloadCopy]
  ],
  gbhForm: [
    [NAV_TAB_NUMBERS.gbhForm.yourDetails, PAGE_NUMBERS.gbhForm.yourDetails, PAGE_ACTION_BUTTON_CODES.gbhForm.cancel],
    [NAV_TAB_NUMBERS.gbhForm.yourDetails, PAGE_NUMBERS.gbhForm.yourDetails, PAGE_ACTION_BUTTON_CODES.gbhForm.downloadCopy],
    [NAV_TAB_NUMBERS.gbhForm.yourDetails, PAGE_NUMBERS.gbhForm.yourDetails, PAGE_ACTION_BUTTON_CODES.gbhForm.switchToEdit],
    [NAV_TAB_NUMBERS.gbhForm.yourDetails, PAGE_NUMBERS.gbhForm.yourDetails, PAGE_ACTION_BUTTON_CODES.gbhForm.delete],
    [NAV_TAB_NUMBERS.gbhForm.theOffer, PAGE_NUMBERS.gbhForm.theOffer, PAGE_ACTION_BUTTON_CODES.gbhForm.cancel],
    [NAV_TAB_NUMBERS.gbhForm.theOffer, PAGE_NUMBERS.gbhForm.theOffer, PAGE_ACTION_BUTTON_CODES.gbhForm.downloadCopy],
    [NAV_TAB_NUMBERS.gbhForm.theOffer, PAGE_NUMBERS.gbhForm.theOffer, PAGE_ACTION_BUTTON_CODES.gbhForm.switchToEdit],
    [NAV_TAB_NUMBERS.gbhForm.theOffer, PAGE_NUMBERS.gbhForm.theOffer, PAGE_ACTION_BUTTON_CODES.gbhForm.delete],
    [NAV_TAB_NUMBERS.gbhForm.responseToTheOffer, PAGE_NUMBERS.gbhForm.responseToTheOffer, PAGE_ACTION_BUTTON_CODES.gbhForm.cancel],
    [NAV_TAB_NUMBERS.gbhForm.responseToTheOffer, PAGE_NUMBERS.gbhForm.responseToTheOffer, PAGE_ACTION_BUTTON_CODES.gbhForm.downloadCopy],
    [NAV_TAB_NUMBERS.gbhForm.responseToTheOffer, PAGE_NUMBERS.gbhForm.responseToTheOffer, PAGE_ACTION_BUTTON_CODES.gbhForm.switchToEdit],
    [NAV_TAB_NUMBERS.gbhForm.responseToTheOffer, PAGE_NUMBERS.gbhForm.responseToTheOffer, PAGE_ACTION_BUTTON_CODES.gbhForm.delete]
  ],
  approverGbhForm: [
    [NAV_TAB_NUMBERS.approverGbhForm.navTabPlaceholder, PAGE_NUMBERS.approverGbhForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverGbhForm.cancel],
    [NAV_TAB_NUMBERS.approverGbhForm.navTabPlaceholder, PAGE_NUMBERS.approverGbhForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverGbhForm.switchToEdit],
    [NAV_TAB_NUMBERS.approverGbhForm.navTabPlaceholder, PAGE_NUMBERS.approverGbhForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverGbhForm.returnForActionWithoutAssessment],
    [NAV_TAB_NUMBERS.approverGbhForm.navTabPlaceholder, PAGE_NUMBERS.approverGbhForm.pagePlaceholder, PAGE_ACTION_BUTTON_CODES.approverGbhForm.downloadCopy]
  ]
};

export const HIDEABLE_GRIDS: { [form: string]: { [grid: string]: HideableGridConfiguration } } = {
  coiForm: {
    versionHistory: {
      showOnFirstLoad: false,
      buttonQuestionPaths: {
        show: [NAV_TAB_NUMBERS.coiForm.attestation, PAGE_NUMBERS.coiForm.attestation, SECTION_CODES.coiForm.versionHistorySection, QUESTION_CODES.coiForm.showVersionHistory],
        hide: [NAV_TAB_NUMBERS.coiForm.attestation, PAGE_NUMBERS.coiForm.attestation, SECTION_CODES.coiForm.versionHistorySection, QUESTION_CODES.coiForm.hideVersionHistory]
      },
      functionNames: { show: 'showVersionHistory', hide: 'hideVersionHistory', shouldShow: 'shouldShowVersionHistory', shouldHide: 'shouldHideVersionHistory' }
    }
  },
  dopiForm: {
    versionHistory: {
      showOnFirstLoad: false,
      buttonQuestionPaths: {
        show: [NAV_TAB_NUMBERS.dopiForm.attestation, PAGE_NUMBERS.dopiForm.attestation, SECTION_CODES.dopiForm.versionHistorySection, QUESTION_CODES.dopiForm.showVersionHistory],
        hide: [NAV_TAB_NUMBERS.dopiForm.attestation, PAGE_NUMBERS.dopiForm.attestation, SECTION_CODES.dopiForm.versionHistorySection, QUESTION_CODES.dopiForm.hideVersionHistory]
      },
      functionNames: { show: 'showVersionHistory', hide: 'hideVersionHistory', shouldShow: 'shouldShowVersionHistory', shouldHide: 'shouldHideVersionHistory' }
    }
  },
  gbhForm: {
    versionHistory: {
      showOnFirstLoad: false,
      buttonQuestionPaths: {
        show: [NAV_TAB_NUMBERS.gbhForm.responseToTheOffer, PAGE_NUMBERS.gbhForm.responseToTheOffer, SECTION_CODES.gbhForm.versionHistorySection, QUESTION_CODES.gbhForm.showVersionHistory],
        hide: [NAV_TAB_NUMBERS.gbhForm.responseToTheOffer, PAGE_NUMBERS.gbhForm.responseToTheOffer, SECTION_CODES.gbhForm.versionHistorySection, QUESTION_CODES.gbhForm.hideVersionHistory]
      },
      functionNames: { show: 'showVersionHistory', hide: 'hideVersionHistory', shouldShow: 'shouldShowVersionHistory', shouldHide: 'shouldHideVersionHistory' }
    }
  },
  approverCoiForm: {
    versionHistory: {
      showOnFirstLoad: false,
      buttonQuestionPaths: {
        show: [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, SECTION_CODES.approverCoiForm.versionHistorySection, QUESTION_CODES.approverCoiForm.showVersionHistory],
        hide: [NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder, PAGE_NUMBERS.approverCoiForm.pagePlaceholder, SECTION_CODES.approverCoiForm.versionHistorySection, QUESTION_CODES.approverCoiForm.hideVersionHistory]
      },
      functionNames: { show: 'showVersionHistory', hide: 'hideVersionHistory', shouldShow: 'shouldShowVersionHistory', shouldHide: 'shouldHideVersionHistory' }
    }
  },
  approverDopiForm: {
    versionHistory: {
      showOnFirstLoad: false,
      buttonQuestionPaths: {
        show: [NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder, PAGE_NUMBERS.approverDopiForm.pagePlaceholder, SECTION_CODES.approverDopiForm.versionHistorySection, QUESTION_CODES.approverDopiForm.showVersionHistory],
        hide: [NAV_TAB_NUMBERS.approverDopiForm.navTabPlaceholder, PAGE_NUMBERS.approverDopiForm.pagePlaceholder, SECTION_CODES.approverDopiForm.versionHistorySection, QUESTION_CODES.approverDopiForm.hideVersionHistory]
      },
      functionNames: { show: 'showVersionHistory', hide: 'hideVersionHistory', shouldShow: 'shouldShowVersionHistory', shouldHide: 'shouldHideVersionHistory' }
    }
  },
  approverGbhForm: {
    versionHistory: {
      showOnFirstLoad: false,
      buttonQuestionPaths: {
        show: [NAV_TAB_NUMBERS.approverGbhForm.navTabPlaceholder, PAGE_NUMBERS.approverGbhForm.pagePlaceholder, SECTION_CODES.approverGbhForm.versionHistorySection, QUESTION_CODES.approverGbhForm.showVersionHistory],
        hide: [NAV_TAB_NUMBERS.approverGbhForm.navTabPlaceholder, PAGE_NUMBERS.approverGbhForm.pagePlaceholder, SECTION_CODES.approverGbhForm.versionHistorySection, QUESTION_CODES.approverGbhForm.hideVersionHistory]
      },
      functionNames: { show: 'showVersionHistory', hide: 'hideVersionHistory', shouldShow: 'shouldShowVersionHistory', shouldHide: 'shouldHideVersionHistory' }
    }
  }
}
